import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { NOINDEX_PRIVATE_SEO } from "../../constants/siteSeo";
import { usePageSeo } from "../../hooks/usePageSeo";
import { useAuth } from "../../context/AuthContext";
import AuthModal from "../../components/auth/AuthModal";
import WalletSignInActions from "../../components/auth/WalletSignInActions";
import GatewayTopNav from "../../components/dashboard/GatewayTopNav";
import GatewaySidebar from "../../components/dashboard/GatewaySidebar";
import GatewayHero from "../../components/dashboard/GatewayHero";
import WalletAccountPanel from "../../components/dashboard/WalletAccountPanel";
import OsharOverviewPanel from "../../components/dashboard/OsharOverviewPanel";
import ActivityTransmissions from "../../components/dashboard/ActivityTransmissions";
import GatewayQuestPanel from "../../components/dashboard/GatewayQuestPanel";
import EcosystemModules from "../../components/dashboard/EcosystemModules";
import MyOsharAccessPanel from "../../components/dashboard/MyOsharAccessPanel";
import TreydoraGatewayLegalStrip from "../../components/legal/TreydoraGatewayLegalStrip";
import { GATEWAY_FOOTER } from "../../data/dashboardMockData";
import "./treydora-gateway-dashboard.css";

const TreydoraGatewayDashboard = () => {
  usePageSeo({
    ...NOINDEX_PRIVATE_SEO,
    title: "Player Dashboard | Treydora",
    description: "Your Treydora gateway — library, quests, Oshar access, and account tools.",
  });

  const { loading, isAuthenticated } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (!isAuthenticated || location.hash !== "#oshar-access") return;
    const t = window.setTimeout(() => {
      document.getElementById("oshar-access")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
    return () => window.clearTimeout(t);
  }, [isAuthenticated, location.hash]);

  if (loading) {
    return (
      <div className="dashboard-shell dashboard-shell--loading">
        <p className="dg-hud">Synchronizing gateway…</p>
      </div>
    );
  }

  return (
    <div className="dashboard-shell dashboard-shell--gateway">
      {!isAuthenticated ? (
        <div className="dashboard-gate dg-gold-ring">
          <h1 className="dashboard-gate__title serif">Treydora Gateway</h1>
          <p className="dashboard-gate__lead">
            Sign in with email, Google/Facebook, or your crypto wallet to unlock the Oshar command center. Dashboard
            access is free — no TRDX balance required for this site.
          </p>
          <div className="dashboard-gate__actions">
            <WalletSignInActions className="dashboard-gate__wallet" />
            <button type="button" className="dg-btn dg-btn--ghost" onClick={() => setAuthOpen(true)}>
              Email sign in
            </button>
          </div>
        </div>
      ) : (
        <>
          <GatewayTopNav />
          <div className="dashboard-body dg-gateway-atmosphere">
            <GatewaySidebar />
            <div className="dashboard-workpad">
              <TreydoraGatewayLegalStrip />
              <main className="dashboard-grid" aria-label="Gateway command center">
                <div className="dashboard-cell dashboard-cell--hero">
                  <GatewayHero />
                </div>
                <div className="dashboard-cell dashboard-cell--right-rail">
                  <WalletAccountPanel />
                  <OsharOverviewPanel />
                  <ActivityTransmissions />
                  <GatewayQuestPanel />
                </div>
                <div className="dashboard-cell dashboard-cell--eco">
                  <EcosystemModules />
                </div>
                <div className="dashboard-cell dashboard-cell--oshar-access">
                  <MyOsharAccessPanel />
                </div>
                <footer className="dashboard-cell dashboard-cell--footer" role="contentinfo">
                  <div className="dg-footer-bar dg-footer-bar--transmission dg-gold-ring dg-premium-ring dg-footer-bar--broadcast">
                    <span className="dg-footer-scan" aria-hidden />
                    <div className="dg-footer-announce">
                      <span className="dg-label-up">Global Announcement</span>
                      <p className="dg-footer-empty">No announcements from Oshar Command.</p>
                      <Link to="/legal" className="dg-btn dg-btn--tiny dg-btn--blue-outline dg-btn--sweep">
                        {GATEWAY_FOOTER.proposalCta}{" "}
                        <i className="fas fa-arrow-right dg-btn__icon" aria-hidden />
                      </Link>
                    </div>
                    <div className="dg-footer-news">
                      <div className="dg-footer-news__head">
                        <span className="dg-label-up">Oshar News Feed</span>
                        <Link to="/blog" className="dg-footer-news__link dg-btn--sweep-plain">
                          View All <i className="fas fa-arrow-right dg-btn__icon" aria-hidden />
                        </Link>
                      </div>
                      <p className="dg-footer-empty">No live feed wired on treydora.com — visit the blog for updates.</p>
                    </div>
                  </div>
                </footer>
              </main>
            </div>
          </div>
        </>
      )}
      <AuthModal isOpen={authOpen} onClose={() => setAuthOpen(false)} />
    </div>
  );
};

export default TreydoraGatewayDashboard;
